/**
 * TrialExpiredModal — full-viewport blocking overlay shown when the tenant's
 * 30-day trial has expired. There's no self-serve upgrade path yet, so the
 * only action is to contact chefsync@chefsync.net (mailto link with a
 * pre-filled subject/body). The user can log out but cannot dismiss the
 * modal by clicking outside.
 */
import { Link, useNavigate } from "react-router-dom";
import { Mail, Sparkles, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const SUPPORT_EMAIL = "chefsync@chefsync.net";

export default function TrialExpiredModal({ user, lang = "es" }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  // Show only when trial_expired_at is set and the plan is currently `free`.
  if (!user?.trial_expired_at || user?.plan !== "free") return null;
  // Master/admin bypass (support access).
  if (["master", "admin"].includes(user?.role)) return null;

  const name = user?.name || user?.email || "";
  const subject = encodeURIComponent(
    lang === "es"
      ? `Reactivar cuenta ChefSync — ${name}`
      : `Reactivate ChefSync account — ${name}`
  );
  const bodyLines = lang === "es"
    ? `Hola,\n\nMi periodo de prueba de ChefSync P&L ha finalizado y me gustaría reactivar mi cuenta.\n\nEmail: ${user?.email}\nNombre: ${name}\n\nGracias.`
    : `Hi,\n\nMy ChefSync P&L trial has ended and I'd like to reactivate my account.\n\nEmail: ${user?.email}\nName: ${name}\n\nThanks.`;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${encodeURIComponent(bodyLines)}`;

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="trial-expired-modal"
    >
      <div className="bg-[var(--surface)] rounded-2xl shadow-2xl max-w-lg w-full border-2 border-[var(--border-color)] overflow-hidden">
        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 px-6 py-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-3 backdrop-blur-sm">
            <Sparkles size={28} strokeWidth={2} className="text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-white">
            {lang === "es" ? "Tu prueba ha terminado" : "Your trial has ended"}
          </h2>
          <p className="text-white/85 text-sm mt-2 leading-snug px-2">
            {lang === "es"
              ? "Gracias por probar ChefSync P&L durante estos 30 días."
              : "Thanks for trying ChefSync P&L for these 30 days."}
          </p>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-[15px] leading-relaxed text-[var(--text-primary)]">
            {lang === "es"
              ? "Para seguir usando todas las funciones, por favor contacta con nosotros y te ayudaremos a activar tu cuenta:"
              : "To keep using all features, please get in touch and we'll help you reactivate your account:"}
          </p>

          <div className="rounded-xl border-2 border-[var(--brand-accent)]/30 bg-[var(--brand-accent)]/5 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--brand-accent)]/15 flex items-center justify-center">
                <Mail size={20} strokeWidth={2} className="text-[var(--brand-accent)]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">
                  {lang === "es" ? "Escríbenos a" : "Email us at"}
                </p>
                <a
                  href={mailto}
                  className="text-base font-semibold text-[var(--brand-accent)] hover:underline break-all"
                  data-testid="trial-expired-mailto"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>

          <a href={mailto} className="block" data-testid="trial-expired-contact-btn">
            <Button className="w-full h-11 bg-[var(--brand-accent)] hover:opacity-90 text-white text-sm font-semibold shadow-sm">
              <Mail size={16} className="mr-2" />
              {lang === "es" ? "Redactar email" : "Compose email"}
            </Button>
          </a>

          <div className="flex items-center gap-2 justify-between pt-1">
            <Link to="/settings" className="text-xs text-[var(--text-muted)] hover:underline">
              {lang === "es" ? "Ver planes" : "See plans"}
            </Link>
            <button
              onClick={doLogout}
              className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] flex items-center gap-1"
              data-testid="trial-expired-logout"
            >
              <LogOut size={12} /> {lang === "es" ? "Cerrar sesión" : "Log out"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
